import React, { useCallback, useMemo } from 'react';
import Checkbox from '@mui/material/Checkbox';
import { alpha } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import Constants from 'style/constants';
import OrganisationUnitTreeItem from 'shared/features/organisation/components/organisation-tree-item/organisation-unit-tree-item';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';

import useMultiSelectOrganizationTree from './hooks';

import './multi-select-organisation-tree.scss';

import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';

type Props = {
  onSelectOrganisationUnit: (selectedNode: OrganisationTreeNode) => void;
  selectedOrganisationUnitIds: string[];
  locationsOnly?: boolean;
  initialVisibleOrganisationUnitId?: string;
};

const getLocationsOnly = (nodes?: OrganisationTreeNode[], result: OrganisationTreeNode[] = []): OrganisationTreeNode[] => {
  if (!nodes) {
    return result;
  }
  for (const node of nodes) {
    if (node.isLocation) {
      result.push(node);
    }
    if (node.childOrganisationUnits?.length > 0) {
      getLocationsOnly(node.childOrganisationUnits, result);
    }
  }
  return result;
};

const MultiSelectOrganisationTree = ({
  onSelectOrganisationUnit,
  selectedOrganisationUnitIds,
  locationsOnly,
  initialVisibleOrganisationUnitId,
}: Readonly<Props>): React.JSX.Element => {
  const { isLoading, isSuccess, organisationUnitTree, expandedNodes, handleToggle, handleSelect, isIndeterminate } =
    useMultiSelectOrganizationTree({
      onSelectOrganisationUnit,
      selectedOrganisationUnitIds,
      initialVisibleOrganisationUnitId,
    });

  const renderCheckbox = useCallback(
    (node: OrganisationTreeNode) => (
      <Checkbox
        indeterminate={isIndeterminate(node)}
        sx={{
          padding: '6px',
          margin: 0,
          marginLeft: locationsOnly || node.childOrganisationUnits.length > 0 ? '0px' : '32px',
          '&:hover': {
            backgroundColor: alpha('#241A0029', 0.16),
          },
        }}
        size="small"
        checked={selectedOrganisationUnitIds?.includes(node.id)}
      />
    ),
    [selectedOrganisationUnitIds, locationsOnly, isIndeterminate],
  );

  const renderChildNodes = (node: OrganisationTreeNode): React.JSX.Element[] | undefined => {
    return node.childOrganisationUnits?.map((child) => (
      <OrganisationUnitTreeItem
        key={child.id}
        itemId={child.id}
        labelText={child.name}
        disableSelection={false}
        highlightColor={'transparent'}
        highlightedHoverColor={alpha(Constants.Colors.secondaryContainer, 0.16)}
        startingIcons={renderCheckbox(child)}
        isRoot={false}
        data-testid={`multiSelectOrganisationTreeItem-${child.id}`}
      >
        {renderChildNodes(child)}
      </OrganisationUnitTreeItem>
    ));
  };

  const renderTree = useMemo(
    () =>
      organisationUnitTree?.map((rootItem) => {
        const childs = locationsOnly
          ? getLocationsOnly(rootItem.childOrganisationUnits)?.map((child) => (
              <OrganisationUnitTreeItem
                key={child.id}
                itemId={child.id}
                labelText={child.name}
                disableSelection={false}
                highlightColor={'transparent'}
                highlightedHoverColor={alpha(Constants.Colors.secondaryContainer, 0.16)}
                startingIcons={renderCheckbox(child)}
                isRoot={false}
                data-testid={`multiSelectOrganisationTreeItem-${child.id}`}
              />
            ))
          : renderChildNodes(rootItem);

        return (
          <OrganisationUnitTreeItem
            key={rootItem.id}
            itemId={rootItem.id}
            labelText={rootItem.name}
            disableSelection={false}
            highlightColor={'transparent'}
            highlightedHoverColor={alpha(Constants.Colors.secondaryContainer, 0.16)}
            startingIcons={renderCheckbox(rootItem)}
            isRoot={false}
            data-testid={`multiSelectOrganisationTreeItem-${rootItem.id}`}
          >
            {childs}
          </OrganisationUnitTreeItem>
        );
      }),
    [organisationUnitTree, selectedOrganisationUnitIds.length, renderCheckbox, locationsOnly],
  );

  return (
    <>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        isSuccess &&
        organisationUnitTree && (
          <div className="tree-view-container" data-testid="multiSelectOrganisationTree">
            <SimpleTreeView
              aria-label="rich object"
              className="tree-view"
              onExpandedItemsChange={handleToggle}
              onItemClick={handleSelect}
              expandedItems={expandedNodes}
            >
              {renderTree}
            </SimpleTreeView>
          </div>
        )
      )}
    </>
  );
};

export default MultiSelectOrganisationTree;
